import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useExercisePlanStore } from '@/stores/exercisePlanStore'
import {
  GripVertical,
  Edit,
  Save,
  X,
  Trash2,
  Play,
  Clock,
  Repeat,
  Target,
  MessageSquare,
  Image as ImageIcon
} from 'lucide-react'

const ExerciseCard = ({ exercise, planId, sectionId, index, isDragging = false }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    sets: exercise.sets,
    reps: exercise.reps,
    rest: exercise.rest,
    tempo: exercise.tempo,
    notes: exercise.notes
  })

  const { updateExercise, deleteExercise, exerciseLibrary } = useExercisePlanStore()

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: exercise.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Get exercise details from library
  const exerciseDetails = exerciseLibrary.find(ex => ex.id === exercise.exerciseId) || {
    name: exercise.name,
    category: 'unknown',
    equipment: 'unknown',
    difficulty: 'unknown',
    image: null,
    videoUrl: null
  }

  const handleSave = () => {
    updateExercise(planId, sectionId, exercise.id, editData)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditData({
      sets: exercise.sets,
      reps: exercise.reps,
      rest: exercise.rest,
      tempo: exercise.tempo,
      notes: exercise.notes
    })
    setIsEditing(false)
  }

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to remove "${exerciseDetails.name}" from this section?`)) {
      deleteExercise(planId, sectionId, exercise.id)
    }
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100'
      case 'intermediate': return 'text-yellow-600 bg-yellow-100'
      case 'advanced': return 'text-red-600 bg-red-100'
      default: return 'text-slate-600 bg-slate-100'
    }
  }

  if (isDragging || isSortableDragging) {
    return (
      <Card className="bg-white shadow-sm border-2 border-blue-300 opacity-90">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
              {exerciseDetails.image ? (
                <img 
                  src={exerciseDetails.image} 
                  alt={exerciseDetails.name}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <ImageIcon className="h-6 w-6 text-slate-400" />
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">{exerciseDetails.name}</h4>
              <p className="text-sm text-slate-600">
                {exercise.sets} sets × {exercise.reps} reps
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="transition-shadow bg-white">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing mt-1 p-1 hover:bg-slate-100 rounded"
            >
              <GripVertical className="h-4 w-4 text-slate-400" />
            </div>

            {/* Exercise Image/Icon */}
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {exerciseDetails.image ? (
                <img 
                  src={exerciseDetails.image} 
                  alt={exerciseDetails.name}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <ImageIcon className="h-6 w-6 text-slate-400" />
              )}
            </div>

            {/* Exercise Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-slate-900 truncate">
                    {exerciseDetails.name}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(exerciseDetails.difficulty)}`}>
                      {exerciseDetails.difficulty}
                    </span>
                    <span className="text-xs text-slate-500 capitalize">
                      {exerciseDetails.category}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  {exerciseDetails.videoUrl && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                      <Play className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    className="h-6 w-6 p-0"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDelete}
                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Sets
                      </label>
                      <Input
                        type="number"
                        value={editData.sets}
                        onChange={(e) => setEditData(prev => ({ ...prev, sets: parseInt(e.target.value) || 0 }))}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Reps
                      </label>
                      <Input
                        value={editData.reps}
                        onChange={(e) => setEditData(prev => ({ ...prev, reps: e.target.value }))}
                        className="h-8 text-sm"
                        placeholder="8-12"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Rest (s)
                      </label>
                      <Input
                        type="number"
                        value={editData.rest}
                        onChange={(e) => setEditData(prev => ({ ...prev, rest: parseInt(e.target.value) || 0 }))}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Tempo
                      </label>
                      <Input
                        value={editData.tempo}
                        onChange={(e) => setEditData(prev => ({ ...prev, tempo: e.target.value }))}
                        className="h-8 text-sm"
                        placeholder="2-1-2-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Notes
                    </label>
                    <Textarea
                      value={editData.notes}
                      onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                      className="text-sm"
                      rows={2}
                      placeholder="Exercise notes or instructions..."
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSave} variant="fitness">
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" onClick={handleCancel} variant="outline">
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center text-slate-600">
                      <Target className="h-3 w-3 mr-1" />
                      <span>{exercise.sets} sets</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <Repeat className="h-3 w-3 mr-1" />
                      <span>{exercise.reps} reps</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{exercise.rest}s rest</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <span className="text-xs">Tempo: {exercise.tempo}</span>
                    </div>
                  </div>
                  
                  {exercise.notes && (
                    <div className="flex items-start gap-1 text-sm text-slate-600 bg-slate-50 rounded p-2">
                      <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span className="text-xs">{exercise.notes}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default ExerciseCard